/**
 * GiggleNest — Add to Cart sparkle animation
 * Fires a sparkle star from the "Add to cart" button to the header cart icon,
 * ONLY after Shopify confirms the item was actually added to the cart.
 *
 * FIX (vs. previous version): the old script fired on the raw button click,
 * before the /cart/add.js request resolved — so a failed or sold-out add
 * still showed a "success" sparkle. This version watches the actual fetch
 * response and only animates on a real 200 with no error payload.
 *
 * INSTALL:
 * 1. Add this file to Assets in the theme editor (Assets > Add new asset).
 * 2. In layout/theme.liquid, add just before </body>:
 *      {{ 'gigglenest-sparkle-to-cart.js' | asset_url | script_tag }}
 *
 * SELECTORS: written for Dawn's default markup. If your cart icon isn't
 * #cart-icon-bubble, inspect it in browser devtools and update CART_ICON_SELECTOR below.
 */

(function () {
  const ADD_TO_CART_SELECTOR = 'button[name="add"]';
  const CART_ICON_SELECTOR = '#cart-icon-bubble';
  const CART_ADD_ENDPOINT = '/cart/add'; // matches /cart/add.js and /cart/add.json

  const SPARKLE_COLOR = '#FFB800'; // GiggleNest yellow
  const SPARKLE_GLOW = '#FFDD88'; // lighter glow halo
  const SPARKLE_SIZE = 34; // px — main star
  const TRAIL_COUNT = 3; // small twinkle particles trailing behind
  const FLIGHT_MS = 1400;
  const ARC_HEIGHT = 70; // px — how high the sparkle arcs upward mid-flight

  // Tracks the button most recently clicked, so that once its underlying
  // /cart/add fetch resolves successfully we know which button to animate from.
  let lastClickedButton = null;

  function getCartIcon() {
    return document.querySelector(CART_ICON_SELECTOR);
  }

  function starSvg(size, color) {
    return (
      '<svg viewBox="0 0 24 24" width="' + size + '" height="' + size + '" fill="' + color + '" ' +
      'style="filter: drop-shadow(0 0 6px ' + SPARKLE_GLOW + ') drop-shadow(0 0 12px ' + color + ');">' +
      '<path d="M12 1.5l2.6 6.9 7.4.6-5.6 4.9 1.7 7.2-6.1-3.9-6.1 3.9 1.7-7.2-5.6-4.9 7.4-.6z"/>' +
      '</svg>'
    );
  }

  function createSparkle() {
    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.position = 'fixed';
    el.style.width = SPARKLE_SIZE + 'px';
    el.style.height = SPARKLE_SIZE + 'px';
    el.style.zIndex = '9999';
    el.style.pointerEvents = 'none';
    el.style.left = '0';
    el.style.top = '0';
    el.style.willChange = 'transform, opacity';
    el.innerHTML = starSvg(SPARKLE_SIZE, SPARKLE_COLOR);
    return el;
  }

  function createTwinkle(size) {
    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.style.position = 'fixed';
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.zIndex = '9998';
    el.style.pointerEvents = 'none';
    el.style.left = '0';
    el.style.top = '0';
    el.style.willChange = 'transform, opacity';
    el.innerHTML = starSvg(size, SPARKLE_GLOW);
    return el;
  }

  function bounceCartIcon() {
    const cartIcon = getCartIcon();
    if (!cartIcon) return;
    cartIcon.style.transition = 'transform 180ms ease';
    cartIcon.style.transform = 'scale(1.25)';
    setTimeout(() => {
      cartIcon.style.transform = 'scale(1)';
    }, 180);
  }

  function shootSparkle(originEl) {
    const cartIcon = getCartIcon();
    if (!originEl || !cartIcon) return;
    // Button may have been removed/replaced by an AJAX re-render between
    // click and fetch-resolve; bail out safely if it's no longer on the page.
    if (!document.body.contains(originEl)) return;

    const startRect = originEl.getBoundingClientRect();
    const endRect = cartIcon.getBoundingClientRect();

    const startX = startRect.left + startRect.width / 2 - SPARKLE_SIZE / 2;
    const startY = startRect.top + startRect.height / 2 - SPARKLE_SIZE / 2;
    const endX = endRect.left + endRect.width / 2 - SPARKLE_SIZE / 2;
    const endY = endRect.top + endRect.height / 2 - SPARKLE_SIZE / 2;

    const midX = (startX + endX) / 2;
    const midY = Math.min(startY, endY) - ARC_HEIGHT;

    // point along a quadratic bezier through start -> mid -> end, used both
    // for the main star's path and to place trailing twinkles behind it
    function pointOnArc(t) {
      const x = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * midX + t * t * endX;
      const y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * midY + t * t * endY;
      return { x, y };
    }

    const sparkle = createSparkle();
    document.body.appendChild(sparkle);
    sparkle.style.transform = `translate(${startX}px, ${startY}px) scale(0.6) rotate(0deg)`;
    sparkle.style.opacity = '1';
    void sparkle.offsetWidth; // force layout before animating

    sparkle.animate(
      [
        { transform: `translate(${startX}px, ${startY}px) scale(0.6) rotate(0deg)`, opacity: 1, offset: 0 },
        { transform: `translate(${pointOnArc(0.15).x}px, ${pointOnArc(0.15).y}px) scale(1.3) rotate(40deg)`, opacity: 1, offset: 0.18 },
        { transform: `translate(${midX}px, ${midY}px) scale(1.1) rotate(140deg)`, opacity: 1, offset: 0.5 },
        { transform: `translate(${endX}px, ${endY}px) scale(0.25) rotate(300deg)`, opacity: 0.4, offset: 1 },
      ],
      {
        duration: FLIGHT_MS,
        easing: 'cubic-bezier(0.25, 0.6, 0.25, 1)',
        fill: 'forwards',
      }
    );

    // small twinkle particles peeling off along the flight path for a sparkle-trail effect
    for (let i = 0; i < TRAIL_COUNT; i++) {
      const delay = 60 + i * 90;
      setTimeout(() => {
        const startT = 0.05 + i * 0.12;
        const p0 = pointOnArc(startT);
        const p1 = pointOnArc(Math.min(startT + 0.25, 1));
        const twinkleSize = 10 + i * 2;
        const twinkle = createTwinkle(twinkleSize);
        document.body.appendChild(twinkle);
        twinkle.style.transform = `translate(${p0.x}px, ${p0.y}px) scale(1) rotate(0deg)`;
        twinkle.style.opacity = '0.9';
        void twinkle.offsetWidth;
        twinkle.animate(
          [
            { transform: `translate(${p0.x}px, ${p0.y}px) scale(1) rotate(0deg)`, opacity: 0.9, offset: 0 },
            { transform: `translate(${p1.x}px, ${p1.y - 10}px) scale(0.3) rotate(90deg)`, opacity: 0, offset: 1 },
          ],
          { duration: 450, easing: 'ease-out', fill: 'forwards' }
        );
        setTimeout(() => twinkle.remove(), 450);
      }, delay);
    }

    setTimeout(() => {
      sparkle.remove();
      bounceCartIcon();
    }, FLIGHT_MS);
  }

  // Step 1: remember which "Add to cart" button was clicked, but do NOT
  // animate yet — we don't know if the add will succeed.
  document.addEventListener('click', function (e) {
    const button = e.target.closest(ADD_TO_CART_SELECTOR);
    if (!button) return;
    if (button.disabled) return; // don't track sold-out / disabled buttons
    lastClickedButton = button;
  });

  // Step 2: watch the actual network call Shopify makes to add the item.
  // Only fire the sparkle once that call resolves successfully.
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = args[0];
    const url = typeof request === 'string' ? request : (request && request.url) || '';
    const isCartAdd = typeof url === 'string' && url.indexOf(CART_ADD_ENDPOINT) !== -1;

    const fetchPromise = originalFetch.apply(this, args);

    if (!isCartAdd) return fetchPromise;

    return fetchPromise.then((response) => {
      // Clone so we don't consume the body the theme's own code still needs to read.
      response
        .clone()
        .json()
        .then((data) => {
          const succeeded = response.ok && !data.errors && !data.description;
          if (succeeded && lastClickedButton) {
            shootSparkle(lastClickedButton);
          }
          lastClickedButton = null;
        })
        .catch(() => {
          // Response wasn't JSON (e.g. a redirect on non-JS fallback) — skip silently.
          lastClickedButton = null;
        });

      return response;
    });
  };
})();